import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./ResetPassword.module.css";
import api from "../../services/api";
import logo from "../../assets/logo_nexus_sem_fundo.png";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });

  // Client-side validation
  const passwordTooShort = newPassword.length > 0 && newPassword.length < 8;
  const passwordsMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;

  const isValid =
    newPassword.length >= 8 && newPassword === confirmPassword;

  const handleBlur = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ newPassword: true, confirmPassword: true });
    if (!isValid) return;

    setLoading(true);
    setApiError("");

    try {
      await api.post("/auth/reset-password", { token, newPassword });
      toast.success("Senha redefinida com sucesso! Redirecionando...");
      setTimeout(() => navigate("/registration"), 2000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 400) {
        setApiError(
          "Este link de redefinição é inválido ou já expirou. Solicite um novo link.",
        );
      } else {
        setApiError("Ocorreu um erro. Tente novamente em instantes.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Guard — no token in URL
  if (!token) {
    return (
      <div className={styles.page}>
        <div className={styles.bgDecor} aria-hidden="true" />
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.brandRow}>
              <div className={styles.logoStub}>
                <img src={logo} alt="Nexus Tech" className={styles.logoImg} />
              </div>
              <div className={styles.brandText}>
                <strong className={styles.brandName}>Nexus Tech</strong>
                <span className={styles.brandTag}>Redefinição de senha</span>
              </div>
            </div>
            <div className={styles.invalidBox} role="alert">
              <span className={styles.invalidIcon} aria-hidden="true">⚠️</span>
              <h2 className={styles.invalidTitle}>Link inválido</h2>
              <p className={styles.invalidMsg}>
                O link de redefinição de senha está ausente ou é inválido.
              </p>
              <Link to="/forgot-password" className={styles.requestLink}>
                Solicitar novo link de redefinição
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.bgDecor} aria-hidden="true" />
      <div className={styles.container}>
        <div className={styles.card}>
          {/* Header */}
          <div className={styles.brandRow}>
            <div className={styles.logoStub}>
              <img src={logo} alt="Nexus Tech" className={styles.logoImg} />
            </div>
            <div className={styles.brandText}>
              <strong className={styles.brandName}>Nexus Tech</strong>
              <span className={styles.brandTag}>Redefinição de senha</span>
            </div>
          </div>

          <h1 className={styles.title}>Criar nova senha</h1>
          <p className={styles.subtitle}>
            Escolha uma nova senha segura para sua conta.
          </p>

          {/* API error banner */}
          {apiError && (
            <div className={styles.errorBox} role="alert" aria-live="polite">
              <span aria-hidden="true">❌</span>
              <span>{apiError}</span>
              {apiError.includes("inválido") && (
                <Link to="/forgot-password" className={styles.retryLink}>
                  Solicitar novo link
                </Link>
              )}
            </div>
          )}

          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            {/* Nova senha */}
            <div className={styles.field}>
              <label htmlFor="newPassword" className={styles.label}>
                Nova senha
              </label>
              <div className={styles.inputWrap}>
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNew ? "text" : "password"}
                  className={`${styles.input} ${
                    touched.newPassword && passwordTooShort ? styles.inputError : ""
                  }`}
                  placeholder="Mínimo 8 caracteres"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() => handleBlur("newPassword")}
                  aria-invalid={touched.newPassword && passwordTooShort}
                  aria-describedby={
                    touched.newPassword && passwordTooShort
                      ? "newPassword-error"
                      : "newPassword-hint"
                  }
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.showBtn}
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Ocultar senha" : "Mostrar senha"}
                  disabled={loading}
                >
                  {showNew ? "Ocultar" : "Mostrar"}
                </button>
              </div>
              {touched.newPassword && passwordTooShort ? (
                <span id="newPassword-error" className={styles.errorText}>
                  A senha deve ter pelo menos 8 caracteres.
                </span>
              ) : (
                <span id="newPassword-hint" className={styles.hintText}>
                  Mínimo 8 caracteres.
                </span>
              )}
            </div>

            {/* Confirmar senha */}
            <div className={styles.field}>
              <label htmlFor="confirmPassword" className={styles.label}>
                Confirmar nova senha
              </label>
              <div className={styles.inputWrap}>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  className={`${styles.input} ${
                    touched.confirmPassword && passwordsMismatch
                      ? styles.inputError
                      : ""
                  }`}
                  placeholder="Repita a nova senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur("confirmPassword")}
                  aria-invalid={touched.confirmPassword && passwordsMismatch}
                  aria-describedby={
                    touched.confirmPassword && passwordsMismatch
                      ? "confirmPassword-error"
                      : undefined
                  }
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className={styles.showBtn}
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Ocultar confirmação" : "Mostrar confirmação"}
                  disabled={loading}
                >
                  {showConfirm ? "Ocultar" : "Mostrar"}
                </button>
              </div>
              {touched.confirmPassword && passwordsMismatch && (
                <span id="confirmPassword-error" className={styles.errorText}>
                  As senhas não coincidem.
                </span>
              )}
            </div>

            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={loading || !isValid}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <span className={styles.spinner} aria-hidden="true" />
                  Redefinindo...
                </>
              ) : (
                "Redefinir senha"
              )}
            </button>
          </form>

          <div className={styles.backRow}>
            <Link to="/registration" className={styles.backLink}>
              ← Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
